import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  AlertTriangle,
  Award,
  BarChart3,
  BellRing,
  CheckCircle2,
  Download,
  ListChecks,
  Percent,
  RefreshCw,
  Users,
} from 'lucide-react';
import {
  adminEmpresaService,
  areaService,
  catalogService,
  notificacaoService,
  reciclagemService,
  relatorioService,
} from '../../shared/services';

type EmpresaOpcao = { id: string; nome: string };

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

function numero(valor: number) {
  return new Intl.NumberFormat('pt-BR').format(valor);
}

export function ReportsPage() {
  const [empresaId, setEmpresaId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [trilhaId, setTrilhaId] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const { data: empresasData } = useQuery({
    queryKey: ['empresas-relatorio'],
    queryFn: () => adminEmpresaService.getEmpresas(),
  });

  const empresas = (Array.isArray(empresasData) ? empresasData : []) as EmpresaOpcao[];

  const { data: areas = [] } = useQuery({
    queryKey: ['areas-relatorio', empresaId],
    queryFn: () => areaService.listar(empresaId || undefined),
  });

  const { data: trilhasData } = useQuery({
    queryKey: ['trilhas-relatorio'],
    queryFn: () => catalogService.getTrilhas(),
  });

  const trilhas = Array.isArray(trilhasData) ? trilhasData : [];

  const filtros = {
    empresaId: empresaId || undefined,
    areaId: areaId || undefined,
    trilhaId: trilhaId || undefined,
  };

  const { data: relatorio, isLoading, isError } = useQuery({
    queryKey: ['relatorio-visao', empresaId, areaId, trilhaId],
    queryFn: () => relatorioService.getVisaoGeral(filtros),
  });

  const exportar = useMutation({
    mutationFn: () => relatorioService.exportarCsv(filtros),
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível exportar o relatório.')),
  });

  const executarLembretes = useMutation({
    mutationFn: () => notificacaoService.executarLembretes(),
    onSuccess: (resultado) => setAviso(`${resultado.enviadas} lembrete(s) enviado(s).`),
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível executar os lembretes.')),
  });

  const executarReciclagem = useMutation({
    mutationFn: () => reciclagemService.executar(),
    onSuccess: (resultado) =>
      setAviso(
        `${resultado.certificadosExpirados} certificado(s) expirado(s), ${resultado.trilhasReatribuidas} trilha(s) reatribuída(s).`,
      ),
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível executar a reciclagem.')),
  });

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 size={28} /> Relatórios
            </h1>
            <p className="text-gray-600 mt-1">
              Conclusão, atraso e certificação por empresa, área e trilha.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => executarReciclagem.mutate()}
              disabled={executarReciclagem.isPending}
              className="btn-secondary flex items-center gap-2 disabled:opacity-60"
            >
              <RefreshCw size={16} />
              {executarReciclagem.isPending ? 'Executando...' : 'Reciclagem'}
            </button>
            <button
              type="button"
              onClick={() => executarLembretes.mutate()}
              disabled={executarLembretes.isPending}
              className="btn-secondary flex items-center gap-2 disabled:opacity-60"
            >
              <BellRing size={16} />
              {executarLembretes.isPending ? 'Executando...' : 'Executar lembretes'}
            </button>
            <button
              type="button"
              onClick={() => exportar.mutate()}
              disabled={exportar.isPending || !relatorio}
              className="btn-primary flex items-center gap-2 disabled:opacity-60"
            >
              <Download size={16} />
              {exportar.isPending ? 'Gerando...' : 'Exportar CSV'}
            </button>
          </div>
        </div>

        {aviso && (
          <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800">{aviso}</div>
        )}

        <div className="card mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Empresa</span>
              <select
                value={empresaId}
                onChange={(event) => {
                  setEmpresaId(event.target.value);
                  setAreaId('');
                }}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todas as empresas</option>
                {empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Área</span>
              <select
                value={areaId}
                onChange={(event) => setAreaId(event.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todas as áreas</option>
                {areas.map((area) => (
                  <option key={area.id} value={area.id}>{area.nome}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Trilha</span>
              <select
                value={trilhaId}
                onChange={(event) => setTrilhaId(event.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todas as trilhas</option>
                {trilhas.map((trilha) => (
                  <option key={trilha.id} value={trilha.id}>{trilha.nome}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : isError || !relatorio ? (
          <div className="card text-center py-10 text-red-600">
            Não foi possível gerar o relatório. Verifique suas permissões.
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              {[
                { rotulo: 'Colaboradores', valor: numero(relatorio.colaboradores), Icon: Users },
                { rotulo: 'Atribuições', valor: numero(relatorio.atribuicoes), Icon: ListChecks },
                { rotulo: 'Concluídas', valor: numero(relatorio.concluidas), Icon: CheckCircle2 },
                { rotulo: 'Atrasadas', valor: numero(relatorio.atrasadas), Icon: AlertTriangle },
                { rotulo: 'Certificados', valor: numero(relatorio.certificados), Icon: Award },
                { rotulo: 'Conclusão', valor: `${relatorio.taxaConclusao}%`, Icon: Percent },
              ].map(({ rotulo, valor, Icon }) => (
                <div key={rotulo} className="card">
                  <Icon className="text-primary-600 mb-2" size={20} />
                  <p className="text-2xl font-bold text-gray-900">{valor}</p>
                  <p className="text-xs text-gray-600">{rotulo}</p>
                </div>
              ))}
            </div>

            <div className="text-xs text-gray-500">
              {relatorio.empresaNome} · {relatorio.areaNome} · {relatorio.trilhaNome} · gerado em{' '}
              {new Date(relatorio.geradoEm).toLocaleString('pt-BR')}
            </div>

            <section className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Por área</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4">Área</th>
                      <th className="py-2 pr-4">Colaboradores</th>
                      <th className="py-2 pr-4">Atribuições</th>
                      <th className="py-2 pr-4">Concluídas</th>
                      <th className="py-2 pr-4">Atrasadas</th>
                      <th className="py-2">Taxa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.areas.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-4 text-gray-500">Nenhum dado no filtro atual.</td>
                      </tr>
                    )}
                    {relatorio.areas.map((area) => (
                      <tr key={area.areaId ?? area.areaNome} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium text-gray-900">{area.areaNome}</td>
                        <td className="py-2 pr-4">{numero(area.colaboradores)}</td>
                        <td className="py-2 pr-4">{numero(area.atribuicoes)}</td>
                        <td className="py-2 pr-4">{numero(area.concluidas)}</td>
                        <td className="py-2 pr-4 text-red-700">{numero(area.atrasadas)}</td>
                        <td className="py-2">{area.taxaConclusao}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Por trilha</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4">Trilha</th>
                      <th className="py-2 pr-4">Atribuídos</th>
                      <th className="py-2 pr-4">Concluídos</th>
                      <th className="py-2 pr-4">Atrasados</th>
                      <th className="py-2">Taxa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.trilhas.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-4 text-gray-500">Nenhuma trilha atribuída no filtro atual.</td>
                      </tr>
                    )}
                    {relatorio.trilhas.map((trilha) => (
                      <tr key={trilha.trilhaId} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium text-gray-900">{trilha.trilhaNome}</td>
                        <td className="py-2 pr-4">{numero(trilha.atribuidos)}</td>
                        <td className="py-2 pr-4">{numero(trilha.concluidos)}</td>
                        <td className="py-2 pr-4 text-red-700">{numero(trilha.atrasados)}</td>
                        <td className="py-2">{trilha.taxaConclusao}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
